// lib/apiError.ts — the ONE way to read an API error.
//
// WHY THIS EXISTS: apiClient's response interceptor rejects with the response BODY
// ({ message, code }), while roughly half the codebase reads the AXIOS shape
// (`err.response.data.code`). On a body-shaped rejection that resolves to `undefined` —
// silently. No throw, no warning; the `if (code === …)` branch simply never runs.
//
// That is not hypothetical: it disabled every coded recovery path in the fax/records UI in
// production (the Send button never disarmed after an indeterminate dispatch, so a second click
// re-faxed a patient's records; "Prepare again" never rendered, so a reclaimed records letter was
// an inescapable dead end). See DESIGN_REVIEW_2026-07-13_nrr-letter-flow.md.
//
// The interceptor now re-attaches `response`/`status` so BOTH shapes resolve. These helpers are the
// shape-agnostic way to read an error, so new code cannot pick the wrong convention:
//
//     import { errCode, errMessage, errStatus } from '../../lib/apiError';
//     if (errCode(err) === 'PREPARE_REQUIRED') { … }
//
// They tolerate every shape an error can arrive in: the body, an AxiosError, a plain Error, a
// string, null.

/**
 * The server's machine-readable error code (e.g. 'PREPARE_REQUIRED'), or undefined.
 *
 * The RESPONSE BODY first — an AxiosError carries its OWN `.code` ('ERR_BAD_REQUEST', 'ECONNABORTED'),
 * so reading `err.code` first would hand back AXIOS' code instead of the server's on any raw
 * AxiosError (bare-axios callers, and the network branch). A caller comparing that against
 * 'PREPARE_REQUIRED' silently never matches — the exact read-the-wrong-shape failure this module
 * exists to prevent. On an intercepted error the two are the same object, so this order costs nothing.
 */
export function errCode(err: unknown): string | undefined {
  const e = err as any;
  const code = e?.response?.data?.code ?? e?.code;
  return typeof code === 'string' ? code : undefined;
}

/** The server's human-readable message, falling back to any Error message. */
export function errMessage(err: unknown): string | undefined {
  const e = err as any;
  // The SERVER's message first. On a raw AxiosError (a network failure, or any caller using bare
  // axios) `e.message` is axios' own "Request failed with status code 409" — useless to an operator.
  const msg = e?.response?.data?.message ?? e?.message;
  return typeof msg === 'string' && msg ? msg : undefined;
}

/** The HTTP status (e.g. 409), or undefined when there is no readable status. */
export function errStatus(err: unknown): number | undefined {
  const e = err as any;
  // Ask the RESPONSE first — it is authoritative. `e.status` is only a convenience alias the
  // interceptor attaches, and it deliberately SKIPS attaching it when the server's body already
  // carries its own `status` field (e.g. `{ status: 'done' }`). Reading `e.status` first would then
  // pick up that non-numeric field, fail the typeof check, and report a real 409 as a NETWORK
  // FAILURE — the same read-the-wrong-shape bug this module exists to prevent.
  const status = e?.response?.status ?? e?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * True when there is no readable HTTP status: an offline / DNS / timeout failure, OR a non-JSON
 * edge response (a CloudFront / App Runner 502 HTML page), which carries no status we can read.
 * NOT a promise that the request never left the machine — only that we have nothing to branch on.
 */
export function isNetworkError(err: unknown): boolean {
  return errStatus(err) === undefined;
}

/**
 * ai.358 (S10 A) - the sentence the SERVER wrote for a failed request, or undefined when the server did not answer
 * (offline, a timeout, an HTML 502, a body without a message) - so a door shows
 *     errServerSentence(err) ?? '<its own fixed sentence>'
 * and never axios' own text ("Network Error", "Request failed with status code 500"). On the status arm it reads
 * ONLY response.data.message, never e.message: the interceptor's fall-through hands back the RAW AxiosError on an
 * empty body, and that error has a numeric status AND axios' message. A refusal raised in the browser before anything
 * left it (DR1's preflight_refusal marker) carries a sentence written for the user and counts as answered.
 */
export function errServerSentence(err: unknown): string | undefined {
  const e = err as any;
  if (e?.preflight_refusal === true) {
    return typeof e.message === 'string' && e.message.trim() ? e.message : undefined;
  }
  if (errStatus(err) === undefined) return undefined;
  const msg = e?.response?.data?.message;
  return typeof msg === 'string' && msg.trim() ? msg : undefined;
}
